import React from "react";
import { PropTypes } from "prop-types";
import { useField } from "formik";
import classnames from "classnames";
import styled from "styled-components";
import Select, { components } from "react-select";

import Asterisk from "../Asterisk/index";
import LoadingInline from "@molecules/LoadingInline";
import { BodySmall, ErrorText, LabelLarge } from "@styled";
import fonts from "@general/fonts";
import colors from "@general/colors";

const fontStyling = {
    color: colors.secondary,
    fontFamily: fonts.sans,
    fontWeight: "500",
    letterSpacing: "0.31px",
    lineHeight: "25px",
    fontSize: "16px"
};

const colorStyles = {
    control: (styles, { isDisabled }) => ({
        ...styles,
        backgroundColor: isDisabled ? colors.ultraLightGray : colors.white,
        borderRadius: 0,
        borderWidth: 1,
        cursor: isDisabled && "not-allowed",
        borderStyle: isDisabled ? "none" : "solid",
        borderColor: colors.lightGray,
        height: "56px",
        paddingLeft: ".7rem",
        pointerEvents: isDisabled && "auto"
    }),
    dropdownIndicator: styles => ({
        ...styles,
        color: colors.darkGray
    }),
    input: (styles, { isDisabled }) => ({
        ...styles,
        backgroundColor: isDisabled && colors.lightGray,
        color: isDisabled && colors.mediumGray
    }),
    option: (styles, { isDisabled, isFocused }) => ({
        ...styles,
        backgroundColor: isFocused && colors.ultraLightGray,
        ...fontStyling,
        color: isDisabled ? colors.lightGray : colors.secondary,
        paddingLeft: "18px",
        paddingRight: "18px"
    }),
    placeholder: (styles, { isDisabled }) => ({
        ...styles,
        ...fontStyling,
        color: isDisabled ? colors.mediumGray : colors.lightGray
    }),
    menu: styles => ({
        ...styles,
        boxShadow: "0 2px 16px rgba(0, 0, 0, 0.14);",
        marginTop: "0",
        paddingLeft: "7px",
        paddingRight: "7px"
    }),
    menuList: styles => ({
        ...styles,
        margin: "15px 0px 23px"
    }),
    singleValue: styles => ({
        ...styles,
        ...fontStyling
    }),
    valueContainer: styles => ({
        ...styles,
        minHeight: "18px"
    })
};

const FixedFooterStyled = styled.div`
    align-items: center;
    border-top: 1px solid ${colors.lightGray};
    cursor: default;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px 18px;
    margin-bottom: -17px;
`;

const FooterTextStyled = styled.p`
    color: ${colors.mediumGray};
    font-family: Red Hat Display;
    font-size: 16px;
    font-weight: 400;
    line-height: 25px;
    letter-spacing: 0.31px;
    margin-bottom: 0;
    text-align: left;
`;

const FooterLinkTextStyled = styled.span`
    color: ${colors.azure};
    font-family: Red Hat Display;
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.94px;
    margin-bottom: 0;

    &:hover {
        cursor: pointer;
    }
`;

const MenuList = ({ children, selectProps, ...props }) => {
    const { fixedFooter } = selectProps;

    if (!fixedFooter)
        return <components.MenuList {...props}>{children}</components.MenuList>;

    const { buttonClick, buttonText, displayText } = fixedFooter;

    return (
        <components.MenuList {...props}>
            {children}
            <FixedFooterStyled>
                <FooterTextStyled>{displayText}</FooterTextStyled>
                <FooterLinkTextStyled onClick={() => buttonClick()}>
                    {buttonText}
                </FooterLinkTextStyled>
            </FixedFooterStyled>
        </components.MenuList>
    );
};

const SelectFancy = ({
    fixedFooter,
    hideLabel,
    label,
    options,
    isDisabled = false,
    isLoading = false,
    isOptionDisabled = () => {},
    menuIsOpen,
    noOptionsMessage = "No options",
    onBlur = () => {},
    onChange,
    ...props
}) => {
    const [field, meta, helpers] = useField(props);
    const { setValue } = helpers;
    const { id, name, required, placeholder } = props;

    const hasError = meta.touched && meta.error;
    const groupClasses = classnames("form-group");

    const getValue = () => {
        if (typeof field.value === "string") {
            return options
                ? options.find(option => option.value === field.value)
                : "";
        }
        // array
        return field.value;
    };

    const handleOnChange = e => {
        if (e === null) {
            setValue("");
            return;
        }
        setValue(Array.isArray(e) ? e : e.value);
    };

    return (
        <div className={groupClasses}>
            <label htmlFor={id || name} className={hideLabel ? "sr-only" : ""}>
                <LabelLarge>
                    {label} {required && <Asterisk />}
                </LabelLarge>
            </label>

            {isLoading && <LoadingInline />}
            {!isLoading && (
                <Select
                    components={{
                        IndicatorSeparator: () => null,
                        MenuList
                    }}
                    fixedFooter={fixedFooter}
                    id={id}
                    isDisabled={isDisabled}
                    isClearable
                    isOptionDisabled={isOptionDisabled}
                    isSearchable
                    menuIsOpen={menuIsOpen}
                    name={name}
                    noOptionsMessage={() => (
                        <ErrorText>{noOptionsMessage}</ErrorText>
                    )}
                    onBlur={onBlur}
                    onChange={onChange || handleOnChange}
                    options={options}
                    placeholder={placeholder}
                    styles={colorStyles}
                    value={getValue() || ""}
                />
            )}

            {hasError ? (
                <BodySmall className="mt-0.25rem text-error">
                    {meta.error}
                </BodySmall>
            ) : null}
        </div>
    );
};

SelectFancy.propTypes = {
    fixedFooter: PropTypes.shape({
        buttonClick: PropTypes.func,
        buttonText: PropTypes.string,
        displayText: PropTypes.string
    }),
    hideLabel: PropTypes.bool,
    label: PropTypes.string,
    id: PropTypes.string,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isOptionDisabled: PropTypes.func,
    menuIsOpen: PropTypes.bool,
    name: PropTypes.string,
    noOptionsMessage: PropTypes.string,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    value: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any
    }),
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
};

export default SelectFancy;
